import { useState, useMemo } from 'react';
import { ProjectStatus, SeedFormat, SeedTreatment } from '@spi/shared';
import type { ProjectFilters as ProjectFiltersType } from '@spi/shared';
import { useReferenceLists } from '../../../api/referenceLists';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';
import { AutocompleteDropdown } from '../shared/AutocompleteDropdown';
import { COUNTRIES } from '../../../i18n/countries';

interface Props {
  filters: ProjectFiltersType;
  onChange: (filters: ProjectFiltersType) => void;
  resultCount?: number;
}

const statusOptions = [
  { value: ProjectStatus.ACTIVE, label: 'Active' },
  { value: ProjectStatus.DRAFT, label: 'Draft' },
  { value: ProjectStatus.COMPLETED, label: 'Completed' },
  { value: ProjectStatus.CANCELLED, label: 'Cancelled' },
];

const seedFormatOptions = [
  { value: SeedFormat.BULK, label: 'Bulk' },
  { value: SeedFormat.PACKET, label: 'Packet' },
  { value: SeedFormat.BOTH, label: 'Bulk + Packet' },
];

const seedTreatmentOptions = [
  { value: SeedTreatment.TREATED, label: 'Treated' },
  { value: SeedTreatment.UNTREATED, label: 'Untreated' },
];

export function ProjectFilters({ filters, onChange, resultCount }: Props) {
  const { data: refLists } = useReferenceLists();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const cropOptions = (refLists?.crop ?? []).map((item) => ({
    value: item.value,
    label: item.value,
  }));

  const zoneOptions = (refLists?.zone ?? []).map((item) => ({
    value: item.value,
    label: `Zone ${item.value}`,
  }));

  const treatmentOptions = (refLists?.treatment ?? []).map((item) => ({
    value: item.value,
    label: item.value,
  }));

  const countryOptions = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}` })),
    [],
  );

  // Count active filters (excluding search, page, limit)
  const activeFilterCount = [
    filters.status?.length ? 1 : 0,
    filters.crop?.length ? 1 : 0,
    filters.country ? 1 : 0,
    filters.zone?.length ? 1 : 0,
    filters.seedFormat?.length ? 1 : 0,
    filters.seedTreatment?.length ? 1 : 0,
    filters.availableTreatments?.length ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  // Build active filter chip data
  const activeChips: Array<{ label: string; onClear: () => void }> = [];
  if (filters.status?.length) {
    for (const s of filters.status) {
      const label = statusOptions.find((o) => o.value === s)?.label ?? s;
      activeChips.push({
        label: `Status: ${label}`,
        onClear: () => onChange({ ...filters, status: filters.status!.filter((v) => v !== s) || undefined, page: 1 }),
      });
    }
  }
  if (filters.crop?.length) {
    for (const c of filters.crop) {
      activeChips.push({
        label: c,
        onClear: () => {
          const remaining = filters.crop!.filter((v) => v !== c);
          onChange({ ...filters, crop: remaining.length ? remaining : undefined, page: 1 });
        },
      });
    }
  }
  if (filters.country) {
    const countryFlag = COUNTRIES.find((c) => c.name === filters.country)?.flag ?? '';
    activeChips.push({
      label: `${countryFlag} ${filters.country}`,
      onClear: () => onChange({ ...filters, country: undefined, page: 1 }),
    });
  }
  if (filters.zone?.length) {
    for (const z of filters.zone) {
      activeChips.push({
        label: `Zone ${z}`,
        onClear: () => {
          const remaining = filters.zone!.filter((v) => v !== z);
          onChange({ ...filters, zone: remaining.length ? remaining : undefined, page: 1 });
        },
      });
    }
  }
  if (filters.seedFormat?.length) {
    for (const f of filters.seedFormat) {
      const label = seedFormatOptions.find((o) => o.value === f)?.label ?? f;
      activeChips.push({
        label: `Format: ${label}`,
        onClear: () => {
          const remaining = filters.seedFormat!.filter((v) => v !== f);
          onChange({ ...filters, seedFormat: remaining.length ? remaining as SeedFormat[] : undefined, page: 1 });
        },
      });
    }
  }
  if (filters.seedTreatment?.length) {
    for (const t of filters.seedTreatment) {
      const label = seedTreatmentOptions.find((o) => o.value === t)?.label ?? t;
      activeChips.push({
        label: `Treatment: ${label}`,
        onClear: () => {
          const remaining = filters.seedTreatment!.filter((v) => v !== t);
          onChange({ ...filters, seedTreatment: remaining.length ? remaining as SeedTreatment[] : undefined, page: 1 });
        },
      });
    }
  }
  if (filters.availableTreatments?.length) {
    for (const t of filters.availableTreatments) {
      activeChips.push({
        label: `My Treatment: ${t}`,
        onClear: () => {
          const remaining = filters.availableTreatments!.filter((v) => v !== t);
          onChange({ ...filters, availableTreatments: remaining.length ? remaining : undefined, page: 1 });
        },
      });
    }
  }

  const clearAllFilters = () => {
    onChange({ page: 1, limit: filters.limit, search: filters.search });
  };

  return (
    <div className="mb-6 space-y-3">
      {/* Search bar + filter toggle row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search with icon */}
        <div className="relative flex-1 max-w-md">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spi-slate/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search projects by name or description..."
            value={filters.search || ''}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined, page: 1 })}
            className="input w-full pl-9"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onChange({ ...filters, search: undefined, page: 1 })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-spi-slate hover:text-red-600 text-sm"
            >
              &times;
            </button>
          )}
        </div>

        {/* Filter toggle button */}
        <button
          type="button"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
            activeFilterCount > 0
              ? 'border-spi-green bg-spi-green/5 text-spi-green-dark'
              : 'border-spi-parchment text-spi-slate hover:border-spi-green hover:text-spi-green-dark'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-spi-green text-white text-xs font-bold">
              {activeFilterCount}
            </span>
          )}
          <svg className={`w-3 h-3 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Result count */}
        {resultCount !== undefined && (
          <div className="flex items-center text-sm text-spi-slate">
            <span className="font-medium text-spi-charcoal">{resultCount}</span>&nbsp;project{resultCount !== 1 ? 's' : ''} found
          </div>
        )}
      </div>

      {/* Expandable filter dropdowns */}
      {filtersExpanded && (
        <div className="flex flex-wrap gap-3 p-4 bg-spi-seed/50 rounded-xl border border-spi-parchment">
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Status</label>
            <MultiSelectDropdown
              options={statusOptions}
              selected={filters.status ?? []}
              onChange={(vals) => onChange({ ...filters, status: vals.length ? vals as ProjectStatus[] : undefined, page: 1 })}
              placeholder="All Statuses"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Crop</label>
            <MultiSelectDropdown
              options={cropOptions}
              selected={filters.crop ?? []}
              onChange={(vals) => onChange({ ...filters, crop: vals.length ? vals : undefined, page: 1 })}
              placeholder="All Crops"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Country</label>
            <AutocompleteDropdown
              options={countryOptions}
              value={filters.country}
              onChange={(val) => onChange({ ...filters, country: val, page: 1 })}
              placeholder="All Countries"
              maxWidth="220px"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Planting Zone</label>
            <MultiSelectDropdown
              options={zoneOptions}
              selected={filters.zone ?? []}
              onChange={(vals) => onChange({ ...filters, zone: vals.length ? vals : undefined, page: 1 })}
              placeholder="All Zones"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Seed Format</label>
            <MultiSelectDropdown
              options={seedFormatOptions}
              selected={filters.seedFormat ?? []}
              onChange={(vals) => onChange({ ...filters, seedFormat: vals.length ? vals as SeedFormat[] : undefined, page: 1 })}
              placeholder="All Formats"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">Seed Treatment</label>
            <MultiSelectDropdown
              options={seedTreatmentOptions}
              selected={filters.seedTreatment ?? []}
              onChange={(vals) => onChange({ ...filters, seedTreatment: vals.length ? vals as SeedTreatment[] : undefined, page: 1 })}
              placeholder="All Treatments"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-spi-slate mb-1">My Treatments</label>
            <MultiSelectDropdown
              options={treatmentOptions}
              selected={filters.availableTreatments ?? []}
              onChange={(vals) => onChange({ ...filters, availableTreatments: vals.length ? vals : undefined, page: 1 })}
              placeholder="Match Compatible"
            />
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-spi-slate font-medium">Active filters:</span>
          {activeChips.map((chip, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-spi-green/10 text-spi-green-dark text-xs font-medium"
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.onClear}
                className="ml-0.5 hover:text-red-600 transition-colors"
              >
                &times;
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs text-spi-slate hover:text-red-600 font-medium transition-colors"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
